import type {
  AudioCaptureOptions,
  LocalAudioTrack,
  LocalParticipant,
  LocalTrackPublication,
  Room,
  ScreenShareCaptureOptions,
  VideoCaptureOptions,
} from 'livekit-client';
import { createLocalTracks, Track } from 'livekit-client';
import type { Observable } from 'rxjs';
import { Subject, map, startWith } from 'rxjs';
import { observeParticipantMedia } from '../../../../node_modules/@livekit/components-core/dist/index';

export type CaptureOptionsBySource<T extends ToggleSource> = T extends Track.Source.Camera
  ? VideoCaptureOptions
  : T extends Track.Source.Microphone
  ? AudioCaptureOptions
  : T extends Track.Source.ScreenShare
  ? ScreenShareCaptureOptions
  : never;

export type MediaToggleType<T extends ToggleSource> = {
  pendingObserver: Observable<boolean>;
  toggle: (forceState?: boolean, captureOptions?: CaptureOptionsBySource<T>) => Promise<void>;
  className: string;
  enabledObserver: Observable<boolean>;
};

export type ToggleSource = Exclude<
  Track.Source,
  Track.Source.ScreenShareAudio | Track.Source.Unknown
>;

export function setupMediaToggle<T extends ToggleSource>(
  source: T,
  room: Room,
  options?: CaptureOptionsBySource<T>,
): MediaToggleType<T> {
  const { localParticipant } = room;

  const getSourceEnabled = (source: ToggleSource, localParticipant: LocalParticipant) => {
    let isEnabled = false;
    switch (source) {
      case Track.Source.Camera:
        isEnabled = localParticipant.isCameraEnabled;
        break;
      case Track.Source.Microphone:
        isEnabled = localParticipant.isMicrophoneEnabled;
        break;
      case Track.Source.ScreenShare:
        isEnabled = localParticipant.isScreenShareEnabled;
        break;
      default:
        break;
    }
    return isEnabled;
  };

  const micEnabledSubject = new Subject<boolean>();

  const enabledObserver = source === Track.Source.Microphone
    ? micEnabledSubject.asObservable()
    : observeParticipantMedia(localParticipant).pipe(
    map((media) => {
      return getSourceEnabled(source, media.participant as LocalParticipant);
    }),
    startWith(getSourceEnabled(source, localParticipant)),
  );

  const pendingSubject = new Subject<boolean>();
  let micTrackPub: LocalTrackPublication | null = null;
  
  const toggle = async (forceState?: boolean, captureOptions?: CaptureOptionsBySource<T>) => {
    try {
      captureOptions ??= options;
      // trigger observable update
      pendingSubject.next(true);
      switch (source) {
        case Track.Source.Camera:
          await localParticipant.setCameraEnabled(
            forceState ?? !localParticipant.isCameraEnabled,
            captureOptions as VideoCaptureOptions,
          );
          break;
        case Track.Source.Microphone: {
          const shouldEnable = forceState ?? !localParticipant.isMicrophoneEnabled;
          if (shouldEnable) {
            if (!micTrackPub) {
              const [track] = await localParticipant.createTracks({ audio: (captureOptions as AudioCaptureOptions | undefined) ?? true });
              micTrackPub = await localParticipant.publishTrack(track as LocalAudioTrack);
            } else {
              await micTrackPub.resumeUpstream();
            }
          } else {
            if (micTrackPub?.track) {
              await micTrackPub.pauseUpstream();
            } else {
              await localParticipant.setMicrophoneEnabled(false);
            }
          }
          micEnabledSubject.next(shouldEnable);
          break;
        }
        case Track.Source.ScreenShare:
          await localParticipant.setScreenShareEnabled(
            forceState ?? !localParticipant.isScreenShareEnabled,
            captureOptions as ScreenShareCaptureOptions,
          );
          break;
        default:
          break;
      }
    } finally {
      pendingSubject.next(false);
      // trigger observable update
    }
  };

  const className: string = 'lk-button';
  return {
    className,
    toggle,
    enabledObserver,
    pendingObserver: pendingSubject.asObservable(),
  };
}

export function setupManualToggle() {
  let state = false;

  const enabledSubject = new Subject<boolean>();

  const pendingSubject = new Subject<boolean>();

  const toggle = async (forceState?: boolean) => {
    pendingSubject.next(true);
    state = forceState ?? !state;
    enabledSubject.next(state);
    pendingSubject.next(false);
  };
  const className: string = 'lk-button';
  return {
    className,
    toggle,
    enabledObserver: enabledSubject.asObservable(),
    pendingObserver: pendingSubject.asObservable(),
  };
}